
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Alert,
    Button,
    Select,
    Spin,
    Tag,
} from "antd";

import {
    CalendarOutlined,
    CheckCircleOutlined,
    DownloadOutlined,
    FilePdfOutlined,
    ReloadOutlined,
    TeamOutlined,
} from "@ant-design/icons";

import {
    pdf,
    Document,
    Page,
    Text,
    View,
    StyleSheet,
} from "@react-pdf/renderer";

import api from "../../../api/api";
import { useAcademicYear } from "../../../AcademicYearContext";

/* ============================================================
   EXCELLENT KIDS FOUNDATION — OFFICIAL BRANDING
============================================================ */

const SCHOOL_NAME = "Excellent Kids Foundation";
const SCHOOL_SUBTITLE = "STUDENT ADMISSIONS & RECORDS";
const REPORT_TITLE = "STUDENT ADMISSION REGISTER";
const REPORT_FOOTER = "OFFICIAL STUDENT RECORD";

const COLORS = {
    navy: "#1B2A41",
    deepBlue: "#24364F",
    khaki: "#C3B091",
    khakiDark: "#9B805B",

    white: "#FFFFFF",
    background: "#F7F5F1",
    surface: "#F3F0EA",
    border: "#D9D0C3",
    borderLight: "#EAE5DD",

    text: "#26313D",
    muted: "#626D78",
    darkText: "#182536",

    success: "#287A45",
    paleGreen: "#EDF6EE",
    paleKhaki: "#F4EEE5",

    tableHeader: "#E8E2D8",
};

const PAGE_SIZE = 100;
const MAX_PAGES = 10000;

/* ============================================================
   SHARED HELPERS
============================================================ */

const getErrorMessage = (error, fallback) => {
    const data = error?.response?.data;

    if (typeof data?.detail === "string") return data.detail;
    if (typeof data?.message === "string") return data.message;

    if (data && typeof data === "object") {
        const firstValue = Object.values(data).flat()[0];

        if (typeof firstValue === "string") return firstValue;
    }

    return error?.message || fallback;
};

const getStudentName = (student) => {
    const fullName = String(student?.full_name || "").trim();

    if (fullName) return fullName;

    return [
        student?.first_name,
        student?.middle_name,
        student?.last_name,
    ]
        .filter(Boolean)
        .map((part) => String(part).trim())
        .filter(Boolean)
        .join(" ") || "N/A";
};

const normalizeGender = (gender) => {
    const value = String(gender || "").trim().toLowerCase();

    if (["m", "male"].includes(value)) return "Male";
    if (["f", "female"].includes(value)) return "Female";

    return gender ? String(gender).trim() : "N/A";
};

const getStudentStatus = (student) =>
    String(
        student?.status ?? student?.admission_status ?? ""
    ).trim() || "N/A";

const getGradeClassName = (student) => {
    const gradeClass = student?.grade_class;

    return (
        student?.grade_class_name ||
        student?.grade_class_display ||
        (typeof gradeClass === "object"
            ? gradeClass?.name || gradeClass?.title
            : "") ||
        ""
    );
};

const extractPage = (data) => {
    if (Array.isArray(data)) {
        return {
            results: data,
            next: null,
            count: data.length,
        };
    }

    const results =
        data?.results ??
        data?.students ??
        data?.data ??
        [];

    return {
        results: Array.isArray(results) ? results : [],
        next: data?.next ?? data?.pagination?.next ?? null,
        count: Number.isFinite(Number(data?.count))
            ? Number(data.count)
            : null,
    };
};

const sanitizeFileName = (value) =>
    String(value || "Student_Register")
        .trim()
        .replace(/[^a-z0-9_-]+/gi, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "") || "Student_Register";

const formatReportDate = () =>
    new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });

const getInitials = (value) =>
    String(value || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase() || "EK";

/* ============================================================
   PDF STYLES — A4 LANDSCAPE
============================================================ */

const pdfStyles = StyleSheet.create({
    page: {
        paddingTop: 27,
        paddingBottom: 42,
        paddingHorizontal: 30,
        backgroundColor: COLORS.white,
        fontFamily: "Helvetica",
        fontSize: 9,
        color: COLORS.text,
    },

    masthead: {
        backgroundColor: COLORS.navy,
        paddingVertical: 14,
        paddingHorizontal: 17,
        borderBottomWidth: 3,
        borderBottomColor: COLORS.khaki,
        marginBottom: 15,
    },

    mastheadTop: {
        flexDirection: "row",
        alignItems: "center",
    },

    brandMark: {
        width: 42,
        height: 42,
        borderWidth: 1.5,
        borderColor: COLORS.khaki,
        borderRadius: 5,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: COLORS.deepBlue,
    },

    brandMarkText: {
        fontSize: 14,
        fontWeight: "bold",
        color: COLORS.khaki,
    },

    brandIdentity: {
        flex: 1,
        marginLeft: 12,
    },

    schoolName: {
        fontSize: 17,
        fontWeight: "bold",
        color: COLORS.white,
        marginBottom: 4,
    },

    schoolSubtitle: {
        fontSize: 8,
        color: COLORS.khaki,
        letterSpacing: 1.1,
    },

    documentBadge: {
        paddingVertical: 7,
        paddingHorizontal: 9,
        borderWidth: 1,
        borderColor: COLORS.khaki,
        borderRadius: 3,
    },

    documentBadgeText: {
        fontSize: 7,
        fontWeight: "bold",
        color: COLORS.khaki,
    },

    mastheadRule: {
        height: 1,
        backgroundColor: COLORS.deepBlue,
        marginTop: 10,
        marginBottom: 8,
    },

    mastheadDescription: {
        fontSize: 8,
        color: "#F0EEE9",
        lineHeight: 1.5,
    },

    reportHeading: {
        marginBottom: 13,
    },

    reportEyebrow: {
        color: COLORS.khakiDark,
        fontSize: 8,
        fontWeight: "bold",
        letterSpacing: 1,
        marginBottom: 5,
    },

    reportTitle: {
        color: COLORS.navy,
        fontSize: 16,
        fontWeight: "bold",
        marginBottom: 5,
    },

    reportDescription: {
        fontSize: 8.5,
        color: COLORS.muted,
        lineHeight: 1.5,
    },

    metadata: {
        flexDirection: "row",
        marginBottom: 13,
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.background,
    },

    metadataItem: {
        flex: 1,
        paddingVertical: 8,
        paddingHorizontal: 9,
        borderRightWidth: 1,
        borderRightColor: COLORS.border,
    },

    metadataItemLast: {
        flex: 1,
        paddingVertical: 8,
        paddingHorizontal: 9,
    },

    metadataLabel: {
        fontSize: 7,
        color: COLORS.muted,
        fontWeight: "bold",
        marginBottom: 4,
    },

    metadataValue: {
        fontSize: 9,
        color: COLORS.navy,
        fontWeight: "bold",
        lineHeight: 1.4,
    },

    sectionHeading: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingVertical: 8,
        paddingHorizontal: 10,
        backgroundColor: COLORS.navy,
        borderLeftWidth: 3,
        borderLeftColor: COLORS.khaki,
    },

    sectionTitle: {
        fontSize: 9,
        fontWeight: "bold",
        color: COLORS.white,
    },

    sectionSubtitle: {
        fontSize: 7.5,
        color: "#E8E4DC",
    },

    table: {
        width: "100%",
        borderWidth: 1,
        borderColor: COLORS.border,
        borderTopWidth: 0,
    },

    tableHeader: {
        flexDirection: "row",
        backgroundColor: COLORS.tableHeader,
        borderBottomWidth: 1,
        borderBottomColor: "#C7BBAA",
        minHeight: 27,
    },

    tableRow: {
        flexDirection: "row",
        minHeight: 26,
        backgroundColor: COLORS.white,
        borderBottomWidth: 0.6,
        borderBottomColor: COLORS.borderLight,
    },

    tableRowAlternate: {
        flexDirection: "row",
        minHeight: 26,
        backgroundColor: "#F8F6F2",
        borderBottomWidth: 0.6,
        borderBottomColor: COLORS.borderLight,
    },

    cell: {
        paddingVertical: 6,
        paddingHorizontal: 7,
        fontSize: 8.5,
        color: COLORS.text,
        justifyContent: "center",
        lineHeight: 1.35,
    },

    headerCell: {
        paddingVertical: 8,
        paddingHorizontal: 7,
        fontSize: 7.5,
        fontWeight: "bold",
        color: COLORS.navy,
        justifyContent: "center",
    },

    numberCell: {
        width: "7%",
        textAlign: "center",
        borderRightWidth: 0.5,
        borderRightColor: COLORS.border,
    },

    nameCell: {
        width: "37%",
    },

    genderCell: {
        width: "13%",
        textAlign: "center",
    },

    classCell: {
        width: "25%",
    },

    statusCell: {
        width: "18%",
        textAlign: "center",
    },

    summarySection: {
        marginTop: 15,
        marginBottom: 10,
    },

    summaryHeading: {
        fontSize: 9,
        fontWeight: "bold",
        color: COLORS.navy,
        marginBottom: 7,
    },

    summaryGrid: {
        flexDirection: "row",
    },

    summaryCard: {
        flex: 1,
        paddingVertical: 9,
        paddingHorizontal: 8,
        marginRight: 6,
        backgroundColor: COLORS.background,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderTopWidth: 3,
        borderTopColor: COLORS.navy,
    },

    summaryCardKhaki: {
        flex: 1,
        paddingVertical: 9,
        paddingHorizontal: 8,
        marginRight: 6,
        backgroundColor: COLORS.paleKhaki,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderTopWidth: 3,
        borderTopColor: COLORS.khaki,
    },

    summaryCardGreen: {
        flex: 1,
        paddingVertical: 9,
        paddingHorizontal: 8,
        backgroundColor: COLORS.paleGreen,
        borderWidth: 1,
        borderColor: "#D5E7CD",
        borderTopWidth: 3,
        borderTopColor: COLORS.success,
    },

    summaryLabel: {
        color: COLORS.muted,
        fontSize: 7,
        fontWeight: "bold",
        marginBottom: 5,
    },

    summaryValue: {
        color: COLORS.navy,
        fontSize: 16,
        fontWeight: "bold",
    },

    summaryValueKhaki: {
        color: COLORS.khakiDark,
        fontSize: 16,
        fontWeight: "bold",
    },

    summaryValueGreen: {
        color: COLORS.success,
        fontSize: 16,
        fontWeight: "bold",
    },

    summaryNote: {
        fontSize: 6.5,
        color: COLORS.muted,
        marginTop: 4,
    },

    documentNote: {
        padding: 9,
        backgroundColor: COLORS.background,
        borderLeftWidth: 3,
        borderLeftColor: COLORS.khaki,
        marginTop: 5,
    },

    documentNoteTitle: {
        fontSize: 7.5,
        fontWeight: "bold",
        color: COLORS.navy,
        marginBottom: 4,
    },

    documentNoteText: {
        fontSize: 7.5,
        color: COLORS.muted,
        lineHeight: 1.5,
    },

    footer: {
        position: "absolute",
        bottom: 17,
        left: 30,
        right: 30,
        paddingTop: 6,
        borderTopWidth: 1,
        borderTopColor: COLORS.border,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },

    footerLeft: {
        flex: 1,
        fontSize: 7,
        color: COLORS.navy,
        fontWeight: "bold",
    },

    footerRight: {
        fontSize: 7,
        color: COLORS.muted,
        textAlign: "right",
    },

    emptyState: {
        padding: 20,
        alignItems: "center",
        justifyContent: "center",
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.background,
    },

    emptyStateTitle: {
        fontSize: 11,
        fontWeight: "bold",
        color: COLORS.navy,
        marginBottom: 6,
    },

    emptyStateText: {
        fontSize: 8,
        color: COLORS.muted,
        textAlign: "center",
    },
});

/* ============================================================
   PDF DOCUMENT
============================================================ */

const StudentGradeClassPDF = ({
    students = [],
    gradeClassName,
    academicYearName,
    generatedDate,
}) => {
    const safeStudents = Array.isArray(students) ? students : [];

    const maleCount = safeStudents.filter(
        (student) => normalizeGender(student?.gender) === "Male"
    ).length;

    const femaleCount = safeStudents.filter(
        (student) => normalizeGender(student?.gender) === "Female"
    ).length;

    const unknownGenderCount =
        safeStudents.length - maleCount - femaleCount;

    return (
        <Document
            title={`${gradeClassName || "Grade Class"} - Student Admission Register`}
            author={SCHOOL_NAME}
            subject="Official Student Admission Register"
            creator="Excellent Kids Foundation School Management System"
            keywords={`Student Register, Admissions, ${academicYearName || ""}`}
        >
            <Page
                size="A4"
                orientation="landscape"
                style={pdfStyles.page}
                wrap
            >
                <View style={pdfStyles.masthead}>
                    <View style={pdfStyles.mastheadTop}>
                        <View style={pdfStyles.brandMark}>
                            <Text style={pdfStyles.brandMarkText}>
                                {getInitials(SCHOOL_NAME)}
                            </Text>
                        </View>

                        <View style={pdfStyles.brandIdentity}>
                            <Text style={pdfStyles.schoolName}>
                                {SCHOOL_NAME}
                            </Text>

                            <Text style={pdfStyles.schoolSubtitle}>
                                {SCHOOL_SUBTITLE}
                            </Text>
                        </View>

                        <View style={pdfStyles.documentBadge}>
                            <Text style={pdfStyles.documentBadgeText}>
                                OFFICIAL RECORD
                            </Text>
                        </View>
                    </View>

                    <View style={pdfStyles.mastheadRule} />

                    <Text style={pdfStyles.mastheadDescription}>
                        Institutional student records and academic
                        administration report.
                    </Text>
                </View>

                <View style={pdfStyles.reportHeading}>
                    <Text style={pdfStyles.reportEyebrow}>
                        ADMISSIONS / STUDENT RECORDS
                    </Text>

                    <Text style={pdfStyles.reportTitle}>
                        {REPORT_TITLE}
                    </Text>

                    <Text style={pdfStyles.reportDescription}>
                        Official student listing for the selected Grade Class
                        and academic year. This report contains{" "}
                        {safeStudents.length} student record
                        {safeStudents.length === 1 ? "" : "s"}.
                    </Text>
                </View>

                <View style={pdfStyles.metadata}>
                    <View style={pdfStyles.metadataItem}>
                        <Text style={pdfStyles.metadataLabel}>
                            GRADE CLASS
                        </Text>
                        <Text style={pdfStyles.metadataValue}>
                            {gradeClassName || "Not specified"}
                        </Text>
                    </View>

                    <View style={pdfStyles.metadataItem}>
                        <Text style={pdfStyles.metadataLabel}>
                            ACADEMIC YEAR
                        </Text>
                        <Text style={pdfStyles.metadataValue}>
                            {academicYearName || "Not specified"}
                        </Text>
                    </View>

                    <View style={pdfStyles.metadataItem}>
                        <Text style={pdfStyles.metadataLabel}>
                            REPORT DATE
                        </Text>
                        <Text style={pdfStyles.metadataValue}>
                            {generatedDate || formatReportDate()}
                        </Text>
                    </View>

                    <View style={pdfStyles.metadataItemLast}>
                        <Text style={pdfStyles.metadataLabel}>
                            TOTAL RECORDS
                        </Text>
                        <Text style={pdfStyles.metadataValue}>
                            {safeStudents.length}
                        </Text>
                    </View>
                </View>

                <View style={pdfStyles.sectionHeading}>
                    <Text style={pdfStyles.sectionTitle}>
                        STUDENT REGISTER
                    </Text>

                    <Text style={pdfStyles.sectionSubtitle}>
                        {gradeClassName || "Selected Grade Class"}
                    </Text>
                </View>

                <View style={pdfStyles.table}>
                    <View style={pdfStyles.tableHeader}>
                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.numberCell,
                            ]}
                        >
                            NO.
                        </Text>

                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.nameCell,
                            ]}
                        >
                            STUDENT FULL NAME
                        </Text>

                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.genderCell,
                            ]}
                        >
                            GENDER
                        </Text>

                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.classCell,
                            ]}
                        >
                            GRADE CLASS
                        </Text>

                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.statusCell,
                            ]}
                        >
                            STATUS
                        </Text>
                    </View>

                    {safeStudents.length > 0 ? (
                        safeStudents.map((student, index) => (
                            <View
                                key={student.id ?? `student-${index}`}
                                style={
                                    index % 2 === 0
                                        ? pdfStyles.tableRow
                                        : pdfStyles.tableRowAlternate
                                }
                                wrap={false}
                            >
                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.numberCell,
                                    ]}
                                >
                                    {index + 1}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.nameCell,
                                    ]}
                                >
                                    {getStudentName(student)}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.genderCell,
                                    ]}
                                >
                                    {normalizeGender(student.gender)}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.classCell,
                                    ]}
                                >
                                    {getGradeClassName(student) ||
                                        gradeClassName ||
                                        "N/A"}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.statusCell,
                                    ]}
                                >
                                    {getStudentStatus(student)}
                                </Text>
                            </View>
                        ))
                    ) : (
                        <View style={pdfStyles.emptyState}>
                            <Text style={pdfStyles.emptyStateTitle}>
                                No Student Records
                            </Text>

                            <Text style={pdfStyles.emptyStateText}>
                                No student records were available for this
                                Grade Class.
                            </Text>
                        </View>
                    )}
                </View>

                <View style={pdfStyles.summarySection} wrap={false}>
                    <Text style={pdfStyles.summaryHeading}>
                        REGISTER SUMMARY
                    </Text>

                    <View style={pdfStyles.summaryGrid}>
                        <View style={pdfStyles.summaryCard}>
                            <Text style={pdfStyles.summaryLabel}>
                                TOTAL STUDENTS
                            </Text>

                            <Text style={pdfStyles.summaryValue}>
                                {safeStudents.length}
                            </Text>

                            <Text style={pdfStyles.summaryNote}>
                                Complete class register
                            </Text>
                        </View>

                        <View style={pdfStyles.summaryCardKhaki}>
                            <Text style={pdfStyles.summaryLabel}>
                                MALE STUDENTS
                            </Text>

                            <Text style={pdfStyles.summaryValueKhaki}>
                                {maleCount}
                            </Text>

                            <Text style={pdfStyles.summaryNote}>
                                Records identified as male
                            </Text>
                        </View>

                        <View style={pdfStyles.summaryCardGreen}>
                            <Text style={pdfStyles.summaryLabel}>
                                FEMALE STUDENTS
                            </Text>

                            <Text style={pdfStyles.summaryValueGreen}>
                                {femaleCount}
                            </Text>

                            <Text style={pdfStyles.summaryNote}>
                                Records identified as female
                            </Text>
                        </View>

                        <View style={pdfStyles.summaryCard}>
                            <Text style={pdfStyles.summaryLabel}>
                                UNSPECIFIED GENDER
                            </Text>

                            <Text style={pdfStyles.summaryValue}>
                                {unknownGenderCount}
                            </Text>

                            <Text style={pdfStyles.summaryNote}>
                                Unrecognized or unspecified value
                            </Text>
                        </View>
                    </View>
                </View>

                <View style={pdfStyles.documentNote} wrap={false}>
                    <Text style={pdfStyles.documentNoteTitle}>
                        RECORDS VERIFICATION
                    </Text>

                    <Text style={pdfStyles.documentNoteText}>
                        This document is generated from student records
                        available in the school management system for the
                        selected academic year. Verify student details against
                        official admission records when necessary.
                    </Text>
                </View>

                <View style={pdfStyles.footer} fixed>
                    <Text style={pdfStyles.footerLeft}>
                        {SCHOOL_NAME.toUpperCase()} | {REPORT_FOOTER}
                    </Text>

                    <Text
                        style={pdfStyles.footerRight}
                        render={({ pageNumber, totalPages }) =>
                            `Page ${pageNumber} of ${totalPages}`
                        }
                    />
                </View>
            </Page>
        </Document>
    );
};

/* ============================================================
   MAIN COMPONENT
============================================================ */

const AdmissionStuListByClass = () => {
    const { selectedYear } = useAcademicYear();

    const academicYearId = selectedYear?.id ?? null;
    const academicYearName = selectedYear?.name || "Not selected";

    const [gradeClasses, setGradeClasses] = useState([]);
    const [selectedGradeClassId, setSelectedGradeClassId] = useState(null);
    const [studentsInSelectedGrade, setStudentsInSelectedGrade] = useState([]);

    const [gradeClassesLoading, setGradeClassesLoading] = useState(false);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState("");
    const [refreshKey, setRefreshKey] = useState(0);

    const gradeClassRequestId = useRef(0);
    const studentRequestId = useRef(0);

    const getToken = useCallback(
        () => localStorage.getItem("access"),
        []
    );

    const handleUnauthorized = useCallback(() => {
        localStorage.removeItem("access");
        window.location.assign("/login");
    }, []);

    /* ========================================================
       LOAD GRADE CLASSES
    ======================================================== */

    const fetchGradeClasses = useCallback(async () => {
        const requestId = ++gradeClassRequestId.current;
        const token = getToken();

        setSelectedGradeClassId(null);
        setStudentsInSelectedGrade([]);
        setGradeClasses([]);
        setError("");

        if (!token) {
            handleUnauthorized();
            return;
        }

        if (!academicYearId) {
            setGradeClassesLoading(false);
            setError("Select an academic year to load Grade Classes.");
            return;
        }

        setGradeClassesLoading(true);

        try {
            const response = await api.get(
                "/api/users/students/grade-classes-with-admissions/",
                {
                    params: {
                        academic_year: academicYearId,
                    },
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (requestId !== gradeClassRequestId.current) return;

            const payload = response.data;

            const items = Array.isArray(payload)
                ? payload
                : payload?.results ??
                  payload?.grade_classes ??
                  payload?.data ??
                  [];

            if (!Array.isArray(items)) {
                throw new Error(
                    "The Grade Class endpoint returned an unsupported response format."
                );
            }

            const uniqueClasses = new Map();

            items.forEach((item) => {
                const id = item?.id ?? item?.grade_class_id;

                const name =
                    item?.name ??
                    item?.grade_class__name ??
                    item?.grade_class_name ??
                    item?.grade_class?.name;

                if (
                    id === null ||
                    id === undefined ||
                    String(id).trim() === ""
                ) {
                    return;
                }

                const key = String(id);

                uniqueClasses.set(key, {
                    value: key,
                    label: String(name || `Grade Class ${key}`),
                    searchText: `${name || ""} ${key}`.toLowerCase(),
                    studentCount: Number(
                        item?.student_count ?? item?.count ?? 0
                    ),
                });
            });

            const options = Array.from(uniqueClasses.values()).sort(
                (a, b) =>
                    a.label.localeCompare(b.label, undefined, {
                        numeric: true,
                        sensitivity: "base",
                    })
            );

            setGradeClasses(options);

            if (options.length === 0) {
                setError(
                    "No Grade Classes with admission records were returned for this academic year. Confirm the selected year and backend endpoint."
                );
            }
        } catch (err) {
            if (requestId !== gradeClassRequestId.current) return;

            console.error("Unable to load Grade Classes:", err);

            if (err?.response?.status === 401) {
                handleUnauthorized();
                return;
            }

            if (err?.response?.status === 404) {
                setError(
                    "The Grade Class endpoint was not found. Verify that grade-classes-with-admissions is registered on the StudentAdmissionView router."
                );
            } else {
                setError(
                    getErrorMessage(
                        err,
                        "Unable to load Grade Classes. Please refresh and try again."
                    )
                );
            }
        } finally {
            if (requestId === gradeClassRequestId.current) {
                setGradeClassesLoading(false);
            }
        }
    }, [academicYearId, getToken, handleUnauthorized]);

    useEffect(() => {
        fetchGradeClasses();

        return () => {
            gradeClassRequestId.current += 1;
        };
    }, [fetchGradeClasses, refreshKey]);

    /* ========================================================
       LOAD ALL STUDENTS FOR SELECTED GRADE CLASS
    ======================================================== */

    const fetchStudentsForGradeClass = useCallback(
        async (gradeClassId) => {
            const requestId = ++studentRequestId.current;
            const token = getToken();

            setStudentsInSelectedGrade([]);
            setError("");

            if (!token) {
                handleUnauthorized();
                return;
            }

            if (
                !academicYearId ||
                gradeClassId === null ||
                gradeClassId === undefined
            ) {
                setStudentsLoading(false);
                return;
            }

            setStudentsLoading(true);

            try {
                let nextUrl = "/api/users/students/";

                let params = {
                    academic_year: academicYearId,
                    grade_class: gradeClassId,
                    page_size: PAGE_SIZE,
                    ordering: "full_name",
                };

                const allStudents = [];
                const seenIds = new Set();
                const visitedPages = new Set();

                let pageCount = 0;

                while (nextUrl) {
                    if (requestId !== studentRequestId.current) return;

                    const query = new URLSearchParams(
                        Object.entries(params).map(([key, value]) => [
                            key,
                            String(value),
                        ])
                    ).toString();

                    const pageKey = `${nextUrl}?${query}`;

                    if (visitedPages.has(pageKey)) {
                        throw new Error(
                            "Repeated pagination detected. The request was stopped to prevent an infinite loop."
                        );
                    }

                    visitedPages.add(pageKey);
                    pageCount += 1;

                    if (pageCount > MAX_PAGES) {
                        throw new Error(
                            "The class result exceeds the supported pagination limit."
                        );
                    }

                    const response = await api.get(nextUrl, {
                        params,
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    if (requestId !== studentRequestId.current) return;

                    const page = extractPage(response.data);

                    page.results.forEach((student) => {
                        if (
                            !student ||
                            student.id === null ||
                            student.id === undefined
                        ) {
                            return;
                        }

                        const id = String(student.id);

                        if (!seenIds.has(id)) {
                            seenIds.add(id);
                            allStudents.push(student);
                        }
                    });

                    nextUrl = page.next || null;

                    // Subsequent DRF next URLs already contain query parameters.
                    params = {};
                }

                if (requestId !== studentRequestId.current) return;

                const sortedStudents = allStudents.sort((a, b) =>
                    getStudentName(a).localeCompare(
                        getStudentName(b),
                        undefined,
                        {
                            sensitivity: "base",
                        }
                    )
                );

                setStudentsInSelectedGrade(sortedStudents);

                if (sortedStudents.length === 0) {
                    setError(
                        "No student records were found for this Grade Class in the selected academic year."
                    );
                }
            } catch (err) {
                if (requestId !== studentRequestId.current) return;

                console.error(
                    "Unable to load class student records:",
                    err
                );

                if (err?.response?.status === 401) {
                    handleUnauthorized();
                    return;
                }

                setStudentsInSelectedGrade([]);

                setError(
                    getErrorMessage(
                        err,
                        "Unable to retrieve all student records for this Grade Class."
                    )
                );
            } finally {
                if (requestId === studentRequestId.current) {
                    setStudentsLoading(false);
                }
            }
        },
        [academicYearId, getToken, handleUnauthorized]
    );

    useEffect(() => {
        if (
            selectedGradeClassId === null ||
            selectedGradeClassId === undefined
        ) {
            setStudentsInSelectedGrade([]);
            setStudentsLoading(false);
            return undefined;
        }

        fetchStudentsForGradeClass(selectedGradeClassId);

        return () => {
            studentRequestId.current += 1;
        };
    }, [
        selectedGradeClassId,
        fetchStudentsForGradeClass,
        refreshKey,
    ]);

    /* ========================================================
       SELECTED CLASS AND SUMMARY COUNTS
    ======================================================== */

    const selectedGradeClass = useMemo(
        () =>
            gradeClasses.find(
                (item) =>
                    String(item.value) === String(selectedGradeClassId)
            ) || null,
        [gradeClasses, selectedGradeClassId]
    );

    const maleCount = useMemo(
        () =>
            studentsInSelectedGrade.filter(
                (student) =>
                    normalizeGender(student?.gender) === "Male"
            ).length,
        [studentsInSelectedGrade]
    );

    const femaleCount = useMemo(
        () =>
            studentsInSelectedGrade.filter(
                (student) =>
                    normalizeGender(student?.gender) === "Female"
            ).length,
        [studentsInSelectedGrade]
    );

    const handleGradeClassChange = (value) => {
        studentRequestId.current += 1;
        setError("");
        setStudentsInSelectedGrade([]);
        setSelectedGradeClassId(value ?? null);
    };

    const handleRefresh = () => {
        setError("");
        setRefreshKey((value) => value + 1);
    };

    /* ========================================================
       DOWNLOAD COMPLETE CLASS PDF
    ======================================================== */

    const handleDownloadPDF = async () => {
        if (!academicYearId) {
            setError(
                "Select an academic year before generating the report."
            );
            return;
        }

        if (
            selectedGradeClassId === null ||
            selectedGradeClassId === undefined
        ) {
            setError("Please select a Grade Class first.");
            return;
        }

        if (studentsLoading) {
            setError(
                "Please wait until the class student records finish loading."
            );
            return;
        }

        if (studentsInSelectedGrade.length === 0) {
            setError(
                "There are no student records available to include in this PDF."
            );
            return;
        }

        try {
            setDownloading(true);
            setError("");

            const className =
                selectedGradeClass?.label || "Grade Class";

            const blob = await pdf(
                <StudentGradeClassPDF
                    students={[...studentsInSelectedGrade]}
                    gradeClassName={className}
                    academicYearName={academicYearName}
                    generatedDate={formatReportDate()}
                />
            ).toBlob();

            if (!blob || blob.size === 0) {
                throw new Error("The generated PDF is empty.");
            }

            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = objectUrl;

            link.download =
                `${sanitizeFileName(SCHOOL_NAME)}_` +
                `${sanitizeFileName(className)}_Student_Register_` +
                `${sanitizeFileName(academicYearName)}.pdf`;

            document.body.appendChild(link);
            link.click();
            link.remove();

            window.setTimeout(
                () => URL.revokeObjectURL(objectUrl),
                2000
            );
        } catch (err) {
            console.error("PDF generation error:", err);

            setError(
                getErrorMessage(
                    err,
                    "Unable to generate the PDF. Please try again."
                )
            );
        } finally {
            setDownloading(false);
        }
    };

    /* ========================================================
       USER INTERFACE
    ======================================================== */

    return (
        <div className="admission-grade-download">
            <div className="admission-grade-download-header">
                <div className="admission-grade-download-icon">
                    <FilePdfOutlined />
                </div>

                <div className="admission-grade-download-heading">
                    <span className="admission-grade-eyebrow">
                        EXCELLENT KIDS FOUNDATION
                    </span>

                    <div className="admission-grade-title">
                        Student Admission Register
                    </div>

                    <span className="admission-grade-description">
                        Select a Grade Class to review student records and
                        generate a complete, print-ready PDF register.
                    </span>
                </div>

                <div className="admission-grade-year">
                    <CalendarOutlined />

                    <div>
                        <span>Academic Year</span>
                        <strong>{academicYearName}</strong>
                    </div>
                </div>
            </div>

            <div className="admission-grade-download-content">
                {error && (
                    <Alert
                        className="admission-grade-alert"
                        type="error"
                        showIcon
                        message="Unable to complete the request"
                        description={error}
                        closable
                        onClose={() => setError("")}
                    />
                )}

                {!academicYearId && (
                    <Alert
                        className="admission-grade-alert"
                        type="warning"
                        showIcon
                        message="Academic year required"
                        description="Select an academic year from the school management system before loading Grade Classes."
                    />
                )}

                <div className="admission-grade-selector">
                    <label htmlFor="admission-grade-class-select">
                        Select Grade Class
                    </label>

                    <Select
                        id="admission-grade-class-select"
                        showSearch
                        allowClear
                        size="large"
                        placeholder="Search or select a Grade Class"
                        value={selectedGradeClassId ?? undefined}
                        options={gradeClasses}
                        loading={gradeClassesLoading}
                        disabled={
                            !academicYearId || gradeClassesLoading
                        }
                        style={{ width: "100%" }}
                        optionFilterProp="searchText"
                        filterOption={(input, option) =>
                            String(
                                option?.searchText ?? option?.label ?? ""
                            )
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        onChange={handleGradeClassChange}
                        notFoundContent={
                            gradeClassesLoading ? (
                                <Spin size="small" />
                            ) : (
                                "No Grade Classes found"
                            )
                        }
                    />
                </div>

                <div className="admission-total-records">
                    <div className="admission-records-icon">
                        <TeamOutlined />
                    </div>

                    <div className="admission-records-copy">
                        <span>Grade Classes available</span>

                        <small>
                            {gradeClassesLoading
                                ? "Loading Grade Classes for the selected academic year..."
                                : `${gradeClasses.length} Grade Class${
                                      gradeClasses.length === 1 ? "" : "es"
                                  } returned by the server`}
                        </small>
                    </div>

                    <strong>
                        {gradeClassesLoading ? "—" : gradeClasses.length}
                    </strong>

                    <Button
                        type="text"
                        icon={<ReloadOutlined />}
                        onClick={handleRefresh}
                        loading={gradeClassesLoading || studentsLoading}
                        aria-label="Refresh Grade Classes and student records"
                        title="Refresh records"
                    />
                </div>

                {gradeClassesLoading && (
                    <div className="admission-grade-loading">
                        <Spin />
                        <span>Loading Grade Classes...</span>
                    </div>
                )}

                {selectedGradeClass && (
                    <>
                        <div className="admission-selected-class">
                            <div className="admission-selected-class-main">
                                <div className="admission-selected-class-icon">
                                    <TeamOutlined />
                                </div>

                                <div>
                                    <span>Selected Grade Class</span>
                                    <strong>
                                        {selectedGradeClass.label}
                                    </strong>
                                </div>
                            </div>

                            <div className="admission-selected-year">
                                <span>Academic Year</span>
                                <strong>{academicYearName}</strong>
                            </div>

                            <Tag className="admission-student-count">
                                <TeamOutlined />
                                {studentsLoading
                                    ? "Loading students..."
                                    : `${studentsInSelectedGrade.length} student${
                                          studentsInSelectedGrade.length === 1
                                              ? ""
                                              : "s"
                                      }`}
                            </Tag>
                        </div>

                        {studentsLoading && (
                            <div className="admission-grade-loading">
                                <Spin />
                                <span>
                                    Loading all student records for{" "}
                                    {selectedGradeClass.label}...
                                </span>
                            </div>
                        )}

                        {!studentsLoading &&
                            studentsInSelectedGrade.length > 0 && (
                                <div className="admission-student-preview">
                                    <div>
                                        <strong>
                                            Student records ready
                                        </strong>

                                        <span>
                                            {studentsInSelectedGrade.length}{" "}
                                            records loaded across all available
                                            pages.
                                        </span>
                                    </div>

                                    <div className="admission-preview-counts">
                                        <Tag className="admission-male-tag">
                                            Male: {maleCount}
                                        </Tag>

                                        <Tag className="admission-female-tag">
                                            Female: {femaleCount}
                                        </Tag>
                                    </div>
                                </div>
                            )}

                        <div className="admission-download-action">
                            <Button
                                type="primary"
                                size="large"
                                icon={<DownloadOutlined />}
                                loading={downloading}
                                disabled={
                                    studentsLoading ||
                                    selectedGradeClassId === null ||
                                    selectedGradeClassId === undefined ||
                                    studentsInSelectedGrade.length === 0
                                }
                                onClick={handleDownloadPDF}
                            >
                                {downloading
                                    ? "Generating PDF..."
                                    : "Download Complete Student Register"}
                            </Button>

                            {!studentsLoading &&
                                studentsInSelectedGrade.length > 0 && (
                                    <span className="admission-download-ready">
                                        <CheckCircleOutlined />
                                        All available class records are ready
                                    </span>
                                )}
                        </div>
                    </>
                )}
            </div>

            <style>{`
                .admission-grade-download {
                    --navy: #1B2A41;
                    --deep-blue: #24364F;
                    --khaki: #C3B091;
                    --khaki-dark: #9B805B;
                    --muted: #626D78;
                    --border: #D9D0C3;

                    width: 100%;
                    overflow: hidden;
                    border-radius: 14px;
                    background: #FFFFFF;
                    border: 1px solid var(--border);
                    box-shadow: 0 8px 26px rgba(27, 42, 65, 0.08);
                    color: #26313D;
                    box-sizing: border-box;
                }

                .admission-grade-download *,
                .admission-grade-download *::before,
                .admission-grade-download *::after {
                    box-sizing: border-box;
                }

                .admission-grade-download-header {
                    min-height: 100px;
                    padding: 20px 23px;
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    background: linear-gradient(
                        115deg,
                        #F0ECE5 0%,
                        #FFFFFF 65%,
                        #F7F3ED 100%
                    );
                    border-bottom: 1px solid var(--border);
                    border-top: 4px solid var(--navy);
                }

                .admission-grade-download-icon {
                    width: 49px;
                    height: 49px;
                    min-width: 49px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 11px;
                    background: var(--navy);
                    color: var(--khaki);
                    border: 1px solid var(--deep-blue);
                    font-size: 22px;
                    box-shadow: 0 4px 10px rgba(27, 42, 65, 0.12);
                }

                .admission-grade-download-heading {
                    min-width: 0;
                    flex: 1;
                }

                .admission-grade-eyebrow {
                    display: block;
                    color: var(--khaki-dark);
                    font-size: 9px;
                    line-height: 1.4;
                    font-weight: 800;
                    letter-spacing: 1.15px;
                    margin-bottom: 5px;
                }

                .admission-grade-title {
                    color: var(--navy);
                    font-size: 19px;
                    line-height: 1.35;
                    font-weight: 800;
                    letter-spacing: -0.35px;
                    overflow-wrap: anywhere;
                }

                .admission-grade-description {
                    display: block;
                    margin-top: 5px;
                    color: var(--muted);
                    font-size: 11px;
                    line-height: 1.7;
                }

                .admission-grade-year {
                    min-width: 158px;
                    max-width: 220px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 11px 13px;
                    border-radius: 9px;
                    background: #FFFFFF;
                    color: var(--navy);
                    border: 1px solid var(--border);
                }

                .admission-grade-year > .anticon {
                    font-size: 18px;
                    color: var(--khaki-dark);
                    flex-shrink: 0;
                }

                .admission-grade-year span,
                .admission-selected-year span {
                    display: block;
                    color: var(--muted);
                    font-size: 8px;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 0.7px;
                    margin-bottom: 4px;
                }

                .admission-grade-year strong,
                .admission-selected-year strong {
                    display: block;
                    color: var(--navy);
                    font-size: 12px;
                    line-height: 1.5;
                    overflow-wrap: anywhere;
                }

                .admission-grade-download-content {
                    padding: 21px 23px 23px;
                }

                .admission-grade-alert {
                    margin-bottom: 15px;
                    border-radius: 9px;
                }

                .admission-grade-selector label {
                    display: block;
                    margin-bottom: 8px;
                    color: var(--navy);
                    font-size: 12px;
                    font-weight: 800;
                }

                .admission-grade-selector .ant-select-selector {
                    min-height: 45px !important;
                    border-radius: 9px !important;
                    border-color: #D4CABB !important;
                    box-shadow: none !important;
                }

                .admission-grade-selector .ant-select-selection-item,
                .admission-grade-selector .ant-select-selection-placeholder {
                    line-height: 43px !important;
                    color: #26313D;
                }

                .admission-grade-selector .ant-select-focused
                    .ant-select-selector {
                    border-color: var(--navy) !important;
                    box-shadow: 0 0 0 3px rgba(27, 42, 65, 0.09) !important;
                }

                .admission-total-records {
                    display: flex;
                    align-items: center;
                    gap: 11px;
                    margin-top: 14px;
                    padding: 12px 14px;
                    border-radius: 10px;
                    background: #F7F5F1;
                    border: 1px solid #E2DACE;
                }

                .admission-records-icon {
                    width: 36px;
                    height: 36px;
                    flex-shrink: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 9px;
                    background: var(--navy);
                    color: var(--khaki);
                    font-size: 16px;
                }

                .admission-records-copy {
                    flex: 1;
                    min-width: 0;
                }

                .admission-records-copy span {
                    display: block;
                    color: var(--navy);
                    font-size: 11px;
                    font-weight: 800;
                }

                .admission-records-copy small {
                    display: block;
                    margin-top: 3px;
                    color: var(--muted);
                    font-size: 10px;
                    line-height: 1.6;
                }

                .admission-total-records > strong {
                    color: var(--navy);
                    font-size: 19px;
                    font-weight: 800;
                    font-variant-numeric: tabular-nums;
                }

                .admission-total-records > .ant-btn {
                    color: var(--navy);
                    border-radius: 8px;
                }

                .admission-total-records > .ant-btn:hover {
                    background: #E8E2D8;
                    color: var(--navy);
                }

                .admission-selected-class {
                    margin-top: 15px;
                    padding: 14px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    flex-wrap: wrap;
                    border-radius: 11px;
                    background: #F7F5F1;
                    border: 1px solid #DED5C8;
                    border-left: 4px solid var(--khaki);
                }

                .admission-selected-class-main {
                    display: flex;
                    align-items: center;
                    gap: 11px;
                    flex: 1;
                    min-width: 190px;
                }

                .admission-selected-class-icon {
                    width: 40px;
                    height: 40px;
                    min-width: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 10px;
                    background: var(--navy);
                    color: var(--khaki);
                    font-size: 17px;
                }

                .admission-selected-class-main span {
                    display: block;
                    color: var(--muted);
                    font-size: 8px;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 0.65px;
                    margin-bottom: 4px;
                }

                .admission-selected-class-main strong {
                    display: block;
                    color: var(--navy);
                    font-size: 14px;
                    line-height: 1.5;
                    overflow-wrap: anywhere;
                }

                .admission-selected-year {
                    min-width: 110px;
                }

                .admission-grade-download .admission-student-count {
                    margin: 0;
                    min-height: 33px;
                    padding: 6px 10px;
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    border: 1px solid #D5E7CD;
                    border-radius: 8px;
                    background: #EDF6EE;
                    color: #246B3B;
                    font-weight: 800;
                    white-space: normal;
                }

                .admission-student-preview {
                    margin-top: 13px;
                    padding: 13px 14px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 12px;
                    flex-wrap: wrap;
                    border: 1px solid #D5E7CD;
                    border-radius: 9px;
                    background: #F7FBF4;
                }

                .admission-student-preview strong,
                .admission-student-preview span {
                    display: block;
                }

                .admission-student-preview strong {
                    color: var(--navy);
                    font-size: 11px;
                }

                .admission-student-preview span {
                    margin-top: 4px;
                    color: var(--muted);
                    font-size: 10px;
                    line-height: 1.6;
                }

                .admission-preview-counts {
                    display: flex;
                    gap: 6px;
                    flex-wrap: wrap;
                }

                .admission-grade-download .admission-male-tag {
                    margin: 0;
                    background: #E9EEF5;
                    border-color: #D0D9E5;
                    color: var(--navy);
                    font-weight: 700;
                    border-radius: 6px;
                }

                .admission-grade-download .admission-female-tag {
                    margin: 0;
                    background: #F4EEE5;
                    border-color: #DED0BB;
                    color: #70583B;
                    font-weight: 700;
                    border-radius: 6px;
                }

                .admission-download-action {
                    margin-top: 17px;
                    display: flex;
                    align-items: center;
                    gap: 13px;
                    flex-wrap: wrap;
                }

                .admission-download-action .ant-btn-primary {
                    min-height: 44px;
                    padding: 0 19px;
                    border-radius: 9px;
                    background: var(--navy);
                    border-color: var(--navy);
                    color: #FFFFFF;
                    font-weight: 800;
                    box-shadow: 0 5px 13px rgba(27, 42, 65, 0.13);
                    transition: background 0.2s ease,
                                border-color 0.2s ease,
                                transform 0.2s ease;
                }

                .admission-download-action .ant-btn-primary:hover:not(:disabled) {
                    background: var(--deep-blue) !important;
                    border-color: var(--deep-blue) !important;
                    color: #FFFFFF !important;
                    transform: translateY(-1px);
                }

                .admission-download-action .ant-btn-primary:disabled {
                    background: #E3E0DA !important;
                    border-color: #E3E0DA !important;
                    color: #7B7D80 !important;
                    box-shadow: none;
                }

                .admission-download-ready {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    color: #287A45;
                    font-size: 10px;
                    font-weight: 700;
                    line-height: 1.6;
                }

                .admission-grade-loading {
                    min-height: 100px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 12px;
                    color: var(--navy);
                    font-size: 12px;
                }

                .admission-grade-download .ant-spin-dot-item {
                    background-color: var(--navy);
                }

                @media (max-width: 760px) {
                    .admission-grade-download-header {
                        align-items: flex-start;
                        flex-wrap: wrap;
                        padding: 17px;
                    }

                    .admission-grade-year {
                        width: 100%;
                        max-width: none;
                    }

                    .admission-grade-download-content {
                        padding: 17px;
                    }

                    .admission-selected-class {
                        gap: 12px;
                    }
                }

                @media (max-width: 520px) {
                    .admission-grade-download {
                        border-radius: 11px;
                    }

                    .admission-grade-download-header {
                        gap: 11px;
                    }

                    .admission-grade-download-icon {
                        width: 41px;
                        height: 41px;
                        min-width: 41px;
                        font-size: 19px;
                    }

                    .admission-grade-title {
                        font-size: 16px;
                    }

                    .admission-grade-description {
                        font-size: 10px;
                    }

                    .admission-grade-download-content {
                        padding: 14px;
                    }

                    .admission-total-records {
                        gap: 8px;
                        padding: 10px;
                    }

                    .admission-records-copy small {
                        font-size: 9px;
                    }

                    .admission-selected-class {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .admission-selected-class-main {
                        min-width: 0;
                        width: 100%;
                    }

                    .admission-selected-year {
                        width: 100%;
                    }

                    .admission-student-preview {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .admission-download-action {
                        align-items: stretch;
                        flex-direction: column;
                    }

                    .admission-download-action .ant-btn {
                        width: 100%;
                        height: auto;
                        min-height: 44px;
                        white-space: normal;
                    }

                    .admission-download-ready {
                        line-height: 1.6;
                    }
                }

                @media print {
                    .admission-grade-download {
                        box-shadow: none;
                    }
                }
            `}</style>
        </div>
    );
};

export default AdmissionStuListByClass;
